import React, { useEffect, useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    RefreshControl,
    ScrollView,
    Image,
    Platform,
    TouchableOpacity,
} from 'react-native';
import { v2GetMyPushNotifications, v2MarkPushNotificationsViewed } from '../../services/v2/api';
import type { V2PushNotification } from '../../types/v2';
import { useAuth } from '../../contexts/AuthContext';
import { COLORS } from '../../theme/themeTokens';

// ── Helpers ──────────────────────────────────────────────────────────────────

/** "14.04.2026" */
const formatDate = (dateString: string): string => {
    const d = new Date(dateString);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
};

/** "17:30" */
const formatTime = (dateString: string): string => {
    const d = new Date(dateString);
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
};

/** Group notifications by date key (dd.MM.yyyy) preserving order */
const groupByDate = (items: V2PushNotification[]): { date: string; items: V2PushNotification[] }[] => {
    const map = new Map<string, V2PushNotification[]>();
    for (const n of items) {
        const key = formatDate(n.created_at);
        const group = map.get(key);
        if (group) { group.push(n); } else { map.set(key, [n]); }
    }
    return Array.from(map.entries()).map(([date, items]) => ({ date, items }));
};

// ── Notification Item ────────────────────────────────────────────────────────

const NotificationItem: React.FC<{ item: V2PushNotification }> = ({ item }) => {
    const isUnread = !item.viewed_at;

    return (
        <View style={styles.notificationItem}>
            {/* Red unread indicator — only render column when unread */}
            {isUnread && (
                <View style={styles.dotColumn}>
                    <View style={styles.unreadDot} />
                </View>
            )}
            <View style={styles.contentContainer}>
                <View style={styles.titleRow}>
                    <Text style={[styles.title, isUnread && styles.titleUnread]}>
                        {item.title}
                    </Text>
                    <Text style={styles.timeText}>{formatTime(item.created_at)}</Text>
                </View>
                <Text style={styles.bodyText}>{item.body}</Text>
            </View>
        </View>
    );
};

// ── Skeleton loader ──────────────────────────────────────────────────────────

const SkeletonItem = () => (
    <View style={styles.notificationItem}>
        <View style={styles.contentContainer}>
            <View style={[styles.skeletonLine, { width: '50%' }]} />
            <View style={[styles.skeletonLine, { width: '80%', marginTop: 6 }]} />
            <View style={[styles.skeletonLine, { width: '30%', marginTop: 6 }]} />
        </View>
    </View>
);

// ── Screen ───────────────────────────────────────────────────────────────────

type TabScreen = 'dashboard' | 'products' | 'reports' | 'support' | 'profile' | 'notifications';

interface NotificationsScreenProps {
    onBack: () => void;
    onNavigate: (screen: TabScreen) => void;
}

const NotificationsScreen: React.FC<NotificationsScreenProps> = ({ onBack, onNavigate }) => {
    // Matches CardScreen so both screens line up vertically. iOS
    // gets 0 (safe-area inset is handled by the system); Android
    // keeps 28 to clear its status bar.
    const statusBarHeight = Platform.OS === 'android' ? 28 : 0;
    const [notifications, setNotifications] = useState<V2PushNotification[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { handleSessionExpired, user } = useAuth();

    /** Loads the list; says whether it did, so only a list the client saw is marked read. */
    const fetchNotifications = useCallback(async (): Promise<boolean> => {
        try {
            setError(null);
            const response = await v2GetMyPushNotifications();
            setNotifications(response.notifications || []);
            return true;
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to load notifications';
            console.error('Failed to fetch notifications:', err);
            if (msg.includes('Session expired') || msg.includes('Not authenticated')) {
                handleSessionExpired(msg);
                return false;
            }
            setError(msg);
            return false;
        }
    }, [handleSessionExpired]);

    // Mark all as viewed when screen opens (fire-and-forget).
    // Skipped under impersonation — would alter state visible to the target
    // user. Backend hard-denies POST /push/mark-viewed regardless.
    const canMarkPushViewed = user?.capabilities?.can_mark_push_viewed ?? true;
    const markAllViewed = useCallback(async () => {
        if (!canMarkPushViewed) {
            return;
        }
        try {
            await v2MarkPushNotificationsViewed();
        } catch {
            // silent — non-critical
        }
    }, [canMarkPushViewed]);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            const loaded = await fetchNotifications();
            setLoading(false);
            // Marked read on the server only once the client has the list in
            // front of them: a failed load used to clear the unread state of
            // notifications nobody had seen.
            if (loaded) {
                markAllViewed();
            }
        };
        load();
    }, [fetchNotifications, markAllViewed]);

    const onRefresh = useCallback(async () => {
        setRefreshing(true);
        await fetchNotifications();
        setRefreshing(false);
    }, [fetchNotifications]);

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <View style={styles.container}>
            {/* Navy header — same layout/paddings as ReportsScreen so the
                title sits at the same vertical position across the two
                screens. Whole row is tappable for Back (matches the recent
                Reports change). */}
            <View style={[styles.headerBackground, { paddingTop: statusBarHeight }]}>
                <TouchableOpacity
                    style={styles.headerRow}
                    activeOpacity={0.7}
                    onPress={onBack}
                >
                    <View style={styles.backButton}>
                        <Image
                            source={require('../../assets/arrow_left_20_white.png')}
                            style={styles.backIcon}
                            resizeMode="contain"
                        />
                    </View>
                    <Text style={styles.headerTitle}>Notifications</Text>
                    <View style={styles.backButton} />
                </TouchableOpacity>
            </View>

            <View style={styles.body}>
                {loading ? (
                    <ScrollView
                        style={styles.list}
                        contentContainerStyle={styles.listContent}
                        showsVerticalScrollIndicator={false}
                    >
                        {[1, 2, 3, 4, 5].map(i => <SkeletonItem key={i} />)}
                    </ScrollView>
                ) : error ? (
                    <View style={styles.centerContainer}>
                        <Text style={styles.errorText}>{error}</Text>
                    </View>
                ) : notifications.length === 0 ? (
                    <View style={styles.centerContainer}>
                        <Text style={styles.emptyTitle}>No Notifications</Text>
                        <Text style={styles.emptySubtitle}>
                            You will see your push notifications here
                        </Text>
                    </View>
                ) : (
                    <ScrollView
                        style={styles.list}
                        contentContainerStyle={styles.listContent}
                        showsVerticalScrollIndicator={false}
                        refreshControl={
                            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
                        }
                    >
                        {groupByDate(notifications).map(section => (
                            <View key={section.date}>
                                <Text style={styles.sectionHeader}>
                                    {section.date}
                                </Text>
                                {section.items.map(n => (
                                    <NotificationItem key={n.id} item={n} />
                                ))}
                            </View>
                        ))}
                    </ScrollView>
                )}
            </View>
        </View>
    );
};

// ── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
    // Layout mirrors ReportsScreen: navy container + navy custom header,
    // pureLight body with rounded top corners pulled up 10dp (marginTop: -10)
    // so the rounded edge sits visually on top of the navy strip. Title
    // indent (statusBarHeight + 44 row + paddingBottom 20) is identical to
    // ReportsScreen so the two screens line up.
    container: {
        flex: 1,
        backgroundColor: COLORS.primaryNavy,
    },
    headerBackground: {
        backgroundColor: COLORS.primaryNavy,
        paddingBottom: 20,
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        height: 44,
    },
    backButton: {
        width: 40,
        height: 40,
        alignItems: 'center',
        justifyContent: 'center',
    },
    backIcon: {
        width: 20,
        height: 20,
        tintColor: COLORS.white,
    },
    headerTitle: {
        color: COLORS.white,
        fontSize: 20,
        fontWeight: '600',
        textAlign: 'center',
    },
    body: {
        flex: 1,
        backgroundColor: COLORS.pureLight,
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
        marginTop: -10,
    },
    list: {
        flex: 1,
    },
    listContent: {
        paddingHorizontal: 16,
        paddingTop: 16,
        paddingBottom: 32,
    },
    // ── Notification Item ─────────────────────────────────────
    notificationItem: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        backgroundColor: COLORS.white,
        borderRadius: 12,
        padding: 14,
        marginBottom: 10,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.04,
        shadowRadius: 4,
        elevation: 0,
    },
    dotColumn: {
        width: 16,
        paddingTop: 4,
        alignItems: 'center',
    },
    unreadDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: COLORS.brandOrange,
    },
    contentContainer: {
        flex: 1,
    },
    titleRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    title: {
        flex: 1,
        fontSize: 17,
        fontWeight: '500',
        color: COLORS.textPrimary,
        marginRight: 8,
    },
    titleUnread: {
        fontWeight: '700',
    },
    timeText: {
        fontSize: 13,
        color: COLORS.blueSteel,
    },
    bodyText: {
        fontSize: 15,
        color: COLORS.slate,
        marginTop: 3,
        lineHeight: 20,
    },
    sectionHeader: {
        fontSize: 15,
        fontWeight: '600',
        color: COLORS.blueSteel,
        marginTop: 12,
        marginBottom: 8,
        paddingLeft: 4,
    },
    // ── Center / Empty / Error ─────────────────────────────────
    centerContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 32,
    },
    emptyTitle: {
        fontSize: 20,
        fontWeight: '600',
        color: COLORS.textPrimary,
        marginBottom: 8,
    },
    emptySubtitle: {
        fontSize: 16,
        color: COLORS.blueSteel,
        textAlign: 'center',
    },
    errorText: {
        fontSize: 16,
        color: COLORS.brandOrange,
        textAlign: 'center',
    },
    // ── Skeleton ─────────────────────────────────────────
    skeletonLine: {
        height: 12,
        borderRadius: 6,
        backgroundColor: COLORS.softGrey,
    },
});

export default NotificationsScreen;
